import { useMemo } from 'react'

import { ChainId } from '@src/configs/chainInfo'
import { ConfigLoadingStatus, StepNames } from '@src/enums'
import { ResultCode } from '@src/enums'
import { IExchangeData } from '@src/types/exchange'
import { IQuoteResponse } from '@src/types/order'
import { isFeatherChain } from '@src/utils/chainUtils'
import { isFeatureEnabled } from '@src/utils/environment'
import { isCrossChainWrappedAsset, isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { useConfigStore } from '@src/zustand_stores'

import { useFetchAllTokens } from './useFetchAllTokens'

interface SwapButtonStateParams {
    sellData: IExchangeData
    buyData?: IExchangeData
    sellAmountString?: string
    isInsufficientBalance?: boolean
    hasInsufficientGas?: boolean
    isBuyOutputLoading?: boolean
    isQuoteInvalid?: boolean
    isCustomRecipientWallet?: boolean
    destinationAddress?: string
    liquiditySource?: string
    isOrderRejected?: boolean
    isQuoteExpired?: boolean
    currentStep?: StepNames
    quoteResponse?: IQuoteResponse
    isGasEstimationFetched?: boolean
    waitingForConnectedWalletInteraction?: boolean
    isBuyChainWalletConnected?: boolean
    isSellChainWalletConnected?: boolean
    isQuoteLoading?: boolean
    isQuoteFetching?: boolean
    isLiquidityUnavailable?: boolean
    isCheckingLiquidity?: boolean
    liquidityError?: string | null
    isTransactionCapped?: boolean
    capButtonText?: string
}

export function useSwapButtonState({
    sellData,
    buyData,
    sellAmountString = '',
    isInsufficientBalance = false,
    hasInsufficientGas = false,
    isBuyOutputLoading = false,
    isQuoteInvalid = false,
    isCustomRecipientWallet = false,
    destinationAddress = '',
    liquiditySource,
    isOrderRejected = false,
    isQuoteExpired = false,
    currentStep = StepNames.UNSTARTED,
    quoteResponse,
    isGasEstimationFetched = false,
    waitingForConnectedWalletInteraction = false,
    isBuyChainWalletConnected = false,
    isSellChainWalletConnected = false,
    isQuoteLoading = false,
    isQuoteFetching = false,
    isLiquidityUnavailable = false,
    isCheckingLiquidity = false,
    liquidityError = null,
    isTransactionCapped = false,
    capButtonText = '',
}: SwapButtonStateParams) {
    const configLoading = useConfigStore(config => config.loading)
    const { isLoading: zustandTokensLoading } = useFetchAllTokens()

    const inputRequired = useMemo(
        () => Number(sellData?.value) === 0 && Number(buyData?.value || 0) === 0,
        [sellData, buyData]
    )

    const isLiquiditySourceUnavailable = useMemo(
        () => !inputRequired && liquiditySource === 'unavailable',
        [inputRequired, liquiditySource]
    )

    const isPendingState = useMemo(
        () =>
            [
                StepNames.DEPOSIT_WETH_PENDING,
                StepNames.APPROVE_TOKENS_PENDING,
                StepNames.CONFIRM_SWAP_PENDING,
                StepNames.MATCHING,
                StepNames.MATCH_CREATED,
                StepNames.MATCH_EXECUTED,
            ].includes(currentStep),
        [currentStep]
    )

    const isCustomWalletValid = useMemo(
        () => isCustomRecipientWallet && destinationAddress !== '',
        [isCustomRecipientWallet, destinationAddress]
    )

    const isConfigurationLoading = useMemo(
        () =>
            configLoading === ConfigLoadingStatus.loading_not_yet_started ||
            configLoading === ConfigLoadingStatus.loading ||
            zustandTokensLoading,
        [configLoading, zustandTokensLoading]
    )

    // Get wallet connection button text based on chain
    const getWalletConnectionText = useMemo(() => {
        // Handle sell-side wallet connection if needed - this has higher priority
        if (!isSellChainWalletConnected) {
            switch (sellData.chainId) {
                case ChainId.Solana:
                    return 'Connect Solana Wallet'
                case ChainId.Tron:
                    return 'Connect Tron Wallet'
                default:
                    return 'Connect EVM Wallet'
            }
        }

        // For buy side, check if custom recipient is enabled but doesn't have a valid address yet
        if (isCustomRecipientWallet && destinationAddress === '') {
            return 'Add Recipient'
        }

        // If using a custom wallet address that's valid, we don't need to connect a buy-side wallet
        if (isCustomRecipientWallet && destinationAddress !== '') {
            return null
        }

        // Only prompt for buy-side wallet if we're not using custom recipient
        if (!isCustomRecipientWallet && !isBuyChainWalletConnected && buyData) {
            switch (buyData.chainId) {
                case ChainId.Solana:
                    return 'Connect Solana Wallet'
                case ChainId.Tron:
                    return 'Connect Tron Wallet'
                default:
                    return 'Connect EVM Wallet'
            }
        }

        return null
    }, [
        isSellChainWalletConnected,
        isBuyChainWalletConnected,
        sellData,
        buyData,
        isCustomRecipientWallet,
        destinationAddress,
    ])

    const hasQuoteErrors = useMemo(
        () =>
            isConfigurationLoading ||
            isQuoteInvalid ||
            isBuyOutputLoading ||
            isLiquiditySourceUnavailable ||
            isQuoteLoading ||
            isQuoteFetching ||
            (!isBuyOutputLoading && quoteResponse && quoteResponse.status === ResultCode.failure),
        [
            isConfigurationLoading,
            isQuoteInvalid,
            isBuyOutputLoading,
            isLiquiditySourceUnavailable,
            isQuoteLoading,
            isQuoteFetching,
            quoteResponse,
        ]
    )

    const hasNonQuoteErrors = useMemo(
        () => isInsufficientBalance || inputRequired,
        [isInsufficientBalance, inputRequired]
    )

    const hasAnyErrors = useMemo(() => {
        let errors = hasNonQuoteErrors

        // Only consider quote-related errors if we rely on quote.
        // Wrap/unwrap does not need this.
        if (buyData && !(isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData))) {
            errors = errors || hasQuoteErrors === true
        }

        return errors
    }, [hasNonQuoteErrors, hasQuoteErrors, sellData, buyData])

    // Check if wallet connection is needed
    const needsWalletConnection = useMemo(
        () => (!isCustomWalletValid && !isBuyChainWalletConnected) || !isSellChainWalletConnected,
        [isCustomWalletValid, isBuyChainWalletConnected, isSellChainWalletConnected]
    )

    const buttonDisabled = useMemo(() => {
        // For Feather orders, don't consider insufficient gas an error
        const isFeatherTrade = buyData && isFeatherChain(buyData.chainId)
        const hasGasError = hasInsufficientGas && !isFeatherTrade

        return (
            hasAnyErrors ||
            waitingForConnectedWalletInteraction ||
            isOrderRejected ||
            hasGasError ||
            (isCustomRecipientWallet && destinationAddress === '') ||
            isPendingState ||
            sellData.value === BigInt(0) ||
            !isGasEstimationFetched ||
            sellAmountString === '0' ||
            // Transaction cap related
            isTransactionCapped ||
            // Liquidity check related
            isLiquidityUnavailable
        )
    }, [
        hasAnyErrors,
        waitingForConnectedWalletInteraction,
        isOrderRejected,
        hasInsufficientGas,
        buyData,
        isCustomRecipientWallet,
        destinationAddress,
        isPendingState,
        sellData,
        isGasEstimationFetched,
        sellAmountString,
        isTransactionCapped,
        isLiquidityUnavailable,
    ])

    const getButtonText = useMemo(() => {
        // First of all check if configuration is loading
        if (isConfigurationLoading) {
            return 'Loading Configuration...'
        }

        // First check for transaction cap text (if provided)
        if (capButtonText) {
            return capButtonText
        }

        if (waitingForConnectedWalletInteraction) {
            return 'Processing...'
        }

        // Check for liquidity availability - this should be high priority
        const isWrapOnlyTrade = buyData ? isWrapOnly(sellData, buyData) : false
        const isUnwrapOnlyTrade = buyData ? isUnWrapOnly(sellData, buyData) : false
        const isCrossChainWrappedToken = buyData ? isCrossChainWrappedAsset(sellData, buyData) : false
        const v2MigrationEnabled = isFeatureEnabled('V2_MIGRATION')

        const isTestnet = isFeatureEnabled('USE_TESTNETS_ONLY')

        // Check for cross-chain wrapped assets - this should be high priority
        const wrappedAssetsMessageEnabled = isFeatureEnabled('WRAPPED_ASSETS_MESSAGE')
        // Don't show the message for Jupiter quotes on same-chain Solana transactions
        const isJupiterSolanaSwap = sellData?.chainId === buyData?.chainId && sellData?.chainId === ChainId.Solana
        if (
            wrappedAssetsMessageEnabled &&
            isCrossChainWrappedToken &&
            !isWrapOnlyTrade &&
            !isUnwrapOnlyTrade &&
            !isTestnet &&
            !isJupiterSolanaSwap
        ) {
            return 'Wrapped Assets Coming Soon'
        }

        // Check again for liquidity status before returning step text
        // This ensures liquidity issues are always shown regardless of step
        if (!isWrapOnlyTrade && !isUnwrapOnlyTrade && v2MigrationEnabled) {
            if (isCheckingLiquidity) {
                return 'Checking Liquidity...'
            }

            if (isLiquidityUnavailable) {
                if (liquidityError) {
                    if (liquidityError.includes('unavailable for target token')) {
                        return 'No Liquidity for Target Token'
                    } else if (liquidityError.includes('unavailable for source token')) {
                        return 'No Liquidity for Source Token'
                    } else {
                        return liquidityError
                    }
                } else {
                    return 'Liquidity Unavailable'
                }
            }
        }

        // Check for input first, before other conditions
        if (inputRequired || sellAmountString === '0.' || sellAmountString === '.') {
            return 'Enter an amount to swap'
        }

        if (isInsufficientBalance) {
            return `Insufficient ${sellData?.token?.symbol ?? ''} balance`
        }

        // Only show gas warning for non-Feather orders
        if (hasInsufficientGas && buyData && !isFeatherChain(buyData.chainId)) {
            return `Insufficient gas for fees`
        }

        // Check for wallet connection needs (moved down in priority)
        const walletConnectionText = getWalletConnectionText
        if (walletConnectionText) {
            return walletConnectionText
        }

        if (buyData && isWrapOnly(sellData, buyData)) {
            if (!isGasEstimationFetched) {
                return 'Simulating gas costs'
            } else {
                return `Wrap ${sellData.token.symbol}`
            }
        } else if (buyData && isUnWrapOnly(sellData, buyData)) {
            return `Unwrap ${sellData.token.symbol}`
        } else {
            if (isCustomRecipientWallet && destinationAddress === '') {
                return 'Add Recipient'
            }
            // We're now using isQuoteLoading instead of isBuyOutputLoading
            // This check is kept for backward compatibility
            if (isQuoteLoading) {
                return 'Getting exchange rate'
            }
            if (!isQuoteLoading && quoteResponse) {
                // Check if we have a V2 API response with success=false
                if (isFeatureEnabled('V2_MIGRATION') && quoteResponse.status === ResultCode.failure) {
                    return quoteResponse.errorMsg || 'Unable to get quote - Please try again'
                } else if (quoteResponse.status === ResultCode.failure) {
                    return 'Unable to get quote - Please try again'
                }
            }

            if (isOrderRejected) {
                return 'Order too small. Please increase your trade size'
            }
            if (isQuoteInvalid || isLiquiditySourceUnavailable) {
                return 'Unable to fill order amount at this time'
            }

            if (isQuoteExpired) {
                return 'Quote Expired - Please Try Again'
            }

            if (!isGasEstimationFetched) {
                return 'Simulating gas costs'
            }

            // Create a mapping of steps to their button text
            const stepToText: { [key in StepNames]?: string } = {
                [StepNames.SWITCH_NETWORKS]: 'Switch Network',
                [StepNames.DEPOSIT_WETH]: `Wrap ${sellData.token?.symbol || 'Token'}`,
                [StepNames.DEPOSIT_WETH_PENDING]: 'Wrapping...',
                [StepNames.APPROVE_TOKENS]: `Approve ${sellData.token?.symbol || 'Token'}`,
                [StepNames.APPROVE_TOKENS_PENDING]: 'Approving...',
                [StepNames.CONFIRM_SWAP]: 'Swap',
                [StepNames.CONFIRM_SWAP_PENDING]: 'Confirming Swap',
                [StepNames.MATCHING]: 'Matching Order',
                [StepNames.MATCH_CREATED]: 'Match Created',
                [StepNames.MATCH_EXECUTED]: 'Match Executed',
            }

            // Get text from mapping or use default
            return stepToText[currentStep] || 'Swap'
        }
    }, [
        capButtonText,
        waitingForConnectedWalletInteraction,
        getWalletConnectionText,
        inputRequired,
        sellAmountString,
        isInsufficientBalance,
        hasInsufficientGas,
        sellData,
        buyData,
        isGasEstimationFetched,
        isCustomRecipientWallet,
        destinationAddress,
        isBuyOutputLoading,
        quoteResponse,
        isConfigurationLoading,
        isOrderRejected,
        isQuoteInvalid,
        isLiquiditySourceUnavailable,
        isLiquidityUnavailable,
        isCheckingLiquidity,
        liquidityError,
        isQuoteExpired,
        currentStep,
    ])

    // Is this a specific case where we need a custom address but can't click?
    const isAddRecipientCase = useMemo(() => getWalletConnectionText === 'Add Recipient', [getWalletConnectionText])

    // Identify error states separately from wallet connection needs
    const hasErrorState = useMemo(() => {
        // Don't consider it an error state if we need wallet connection
        if (needsWalletConnection) {
            return false
        }

        // For Feather orders, don't consider insufficient gas an error
        const isFeatherTrade = buyData && isFeatherChain(buyData.chainId)
        const hasGasError = hasInsufficientGas && !isFeatherTrade

        return (
            isInsufficientBalance ||
            hasGasError ||
            hasAnyErrors ||
            waitingForConnectedWalletInteraction ||
            isOrderRejected ||
            !isGasEstimationFetched ||
            isQuoteInvalid ||
            isLiquiditySourceUnavailable ||
            isQuoteExpired
        )
    }, [
        needsWalletConnection,
        buyData,
        isInsufficientBalance,
        hasInsufficientGas,
        hasAnyErrors,
        waitingForConnectedWalletInteraction,
        isOrderRejected,
        isGasEstimationFetched,
        isQuoteInvalid,
        isLiquiditySourceUnavailable,
        isQuoteExpired,
    ])

    return {
        buttonDisabled,
        buttonText: getButtonText,
        isPendingState,
        hasAnyErrors,
        isCustomWalletValid,
        needsWalletConnection,
        getWalletConnectionText,
        isAddRecipientCase,
        hasErrorState,
    }
}
